// asthmaScoring.ts
import { SeverityLevel } from '../utils/asthmaTypes';

/**
 * params for calculations
 */
export type AsthmaScoringInputs = {
  age: string;
  respiratoryRate: string;
  oxygenSaturation: string;
  oxygenSupport: boolean;
  auscultation: string; 
  retractions: string;  
  dyspnea: string;      
};

// === helpers funcs for score calculations===

function calculateRRScore(age: string, rr: string): number {
  const rrNum = parseInt(rr);
  const ageNum = parseInt(age);

  if (Number.isNaN(rrNum) || Number.isNaN(ageNum)) return 0;

  if (ageNum >= 1 && ageNum <= 3) {
    if (rrNum >= 40) return 1;
    if (rrNum >= 35) return 2;
    return 3;
  } else if (ageNum >= 4 && ageNum <= 5) {
    if (rrNum >= 36) return 1;
    if (rrNum >= 31) return 2;
    return 3;
  } else if (ageNum >= 6 && ageNum <= 12) {
    if (rrNum >= 31) return 1;
    if (rrNum >= 27) return 2;
    return 3;
  } else if (ageNum > 12) {
    if (rrNum >= 28) return 1;
    if (rrNum >= 24) return 2;
    return 3;
  }
  return 0;
}

function calculateO2Score(saturation: string, support: boolean): number {
  const satNum = parseInt(saturation);
  if (Number.isNaN(satNum)) return 0;

  if (support || satNum < 90) return 3;
  if (satNum >= 90 && satNum <= 95) return 2;
  if (satNum > 95) return 1;
  return 0;
}

/**
 * total score
 */
export function calculateAsthmaScore(inputs: AsthmaScoringInputs): number {
  const {
    age,
    respiratoryRate,
    oxygenSaturation,
    oxygenSupport,
    auscultation,
    retractions,
    dyspnea,
  } = inputs;

  let totalScore = 0;

  if (age && respiratoryRate) totalScore += calculateRRScore(age, respiratoryRate);
  if (oxygenSaturation) totalScore += calculateO2Score(oxygenSaturation, oxygenSupport);

  const auscultationScores = { mild: 1, moderate: 2, severe: 3 } as const;
  if (auscultation) totalScore += auscultationScores[auscultation as keyof typeof auscultationScores] || 0;

  const retractionScores = { none: 1, moderate: 2, severe: 3 } as const;
  if (retractions) totalScore += retractionScores[retractions as keyof typeof retractionScores] || 0;

  const dyspneaScores = { mild: 1, moderate: 2, severe: 3 } as const;
  if (dyspnea) totalScore += dyspneaScores[dyspnea as keyof typeof dyspneaScores] || 0;

  return totalScore;
}

/**
 * map score to severity + treatment reccomendation
 */
export function mapScoreToSeverity(totalScore: number): {
  severityLabel: string;
  severityLevel: SeverityLevel | null;
  recommendation: string;
} {
  if (totalScore >= 5 && totalScore <= 7) {
    return {
      severityLabel: 'קל',
      severityLevel: 'MILD',
      recommendation: 'מתן אינהלציה ונטולין, הערכה חוזרת אחרי 20–30 דקות',
    };
  }

  if (totalScore >= 8 && totalScore <= 11) {
    return {
      severityLabel: 'בינוני',
      severityLevel: 'MODERATE',
      recommendation: 'חמצן + ונטולין כל 20 דקות X3 עם ארובנט + סטרואידים',
    };
  }

  if (totalScore >= 12 && totalScore <= 15) {
    return {
      severityLabel: 'קשה',
      severityLevel: 'SEVERE',
      recommendation: 'אזור השגחה + ניטור + אינהלציה רציפה + סטרואידים IV',
    };
  }

  

  return { severityLabel: '', severityLevel: null, recommendation: '' };
}

/**
 * 
 * calculate num of does for eaach round
 */
export function getMaxDosesForSeverity(
  severity: SeverityLevel | null,
  round: 1 | 2 | 3 = 1
): number {
  if (!severity) return 1;

  // Special case: Third treatment in a severe condition → 4 doses
  if (severity === 'SEVERE' && round === 3) {
    return 4;
  }

  switch (severity) {
    case 'MILD': return 1;
    case 'MODERATE': return 3;
    case 'SEVERE': return 3;
    default: return 1;
  }
}